import React from "react";
import { Drawer } from "@mui/material";
import { JcCommonProps, JcUtils } from "../utils/JcUtils";
import { JcColumn, JcRow } from "./JcBox";
import { JcIconButton } from "./button/JcIconButton";
import { JcIconType } from "./icon/JcIcon";
import { JcBuiltInIcon } from "./icon/JcBuiltInIcons";

export interface JcDrawerProps extends JcCommonProps {
    readonly open: boolean;
    readonly onClose: () => void;
    /** Optional header content rendered beside the close button. */
    readonly title?: React.ReactNode;
    /** Edge the drawer slides in from. Defaults to "right". */
    readonly anchor?: "left" | "right";
    /** Hides the built-in header row (close button + title). */
    readonly hideHeader?: boolean;
    readonly children: React.ReactNode;
}

/**
 * A generic content drawer: an overlay panel that slides in from a screen edge,
 * full-height (and full-width on small screens). Unlike {@link JcAppDrawer},
 * which is wired for nav, this hosts arbitrary content — a notifications tray, a
 * details panel, a filter sidebar. Wraps MUI's `Drawer`, keeping that dependency
 * inside the SDK.
 */
export const JcDrawer = (props: JcDrawerProps): React.ReactElement => {
    const commonProps = JcUtils.commonProps(props, ["jc-drawer"]);

    return (
        <Drawer
            anchor={props.anchor ?? "right"}
            open={props.open}
            onClose={props.onClose}
            // Allow modals/popups opened from inside the drawer (which portal to
            // document.body, outside the drawer) to receive focus and clicks.
            disableEnforceFocus
            // Below the modal popup overlay (jc-popup-overlay = 1000) so a JcPopup
            // opened from inside the drawer stacks above it, while staying well
            // above page content: content < drawer < modal dialog.
            sx={{ zIndex: 900 }}
            PaperProps={{
                className: "jc-drawer-paper",
            }}
        >
            <JcColumn {...commonProps} spacing>
                {!props.hideHeader && (
                    <JcRow className="jc-drawer-top-bar" alignCenter>
                        {props.title}
                        <JcIconButton
                            className="jc-drawer-close-icon"
                            onClick={props.onClose}
                            iconProps={{ type: JcIconType.BUILT_IN, icon: JcBuiltInIcon.X_MARK }}
                        />
                    </JcRow>
                )}
                {props.children}
            </JcColumn>
        </Drawer>
    );
};
